import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { publicAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Wallet, Trophy, TrendingUp, Bitcoin, Shield, ArrowRightLeft, Zap,
  Gift, Smile, ArrowRight, Menu, X, Sun, Moon, Globe, DollarSign, Search, Users, ChevronDown, ChevronUp
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';

const ICON_MAP = {
  wallet: Wallet, trophy: Trophy, chart: TrendingUp, bitcoin: Bitcoin,
  shield: Shield, arrow: ArrowRightLeft, zap: Zap, gift: Gift, smile: Smile,
  dollar: DollarSign,
};

const LangIcon = ({ name, className }) => {
  const C = ICON_MAP[name] || Wallet;
  return <C className={className} />;
};

const formatCurrency = (value, currency) => {
  if (value == null) return '—';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD', maximumFractionDigits: 2 }).format(value);
  } catch (e) {
    return `${(value || 0).toLocaleString()} ${currency || ''}`;
  }
};

export const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [cfg, setCfg] = useState(null);
  const [tvl, setTvl] = useState(null);
  const [locale, setLocale] = useState(localStorage.getItem('landing_locale') || 'en');
  const [mobileNav, setMobileNav] = useState(false);
  const [expandedFeature, setExpandedFeature] = useState(null);

  useEffect(() => {
    // Fetch landing config with feature toggle status
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/landing-with-features?locale=${locale}`)
      .then(r => r.json())
      .then(data => setCfg(data))
      .catch(() => {
        // Fallback to regular endpoint if new one fails
        publicAPI.getLanding(locale).then((r) => setCfg(r.data)).catch(() => {});
      });
    publicAPI.getLandingTVL().then((r) => setTvl(r.data)).catch(() => {});
  }, [locale]);

  useEffect(() => {
    if (user) {
      // logged-in users skip landing
      navigate(user.role === 'admin' ? '/admin/dashboard' : '/user/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const switchLocale = (l) => {
    setLocale(l);
    localStorage.setItem('landing_locale', l);
  };

  if (!cfg) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  const sections = cfg.sections || {};
  const brandName = cfg.brand?.name || 'Matrise Wallet';
  const backendBase = process.env.REACT_APP_BACKEND_URL || '';
  const resolveAsset = (u) => (!u ? '' : (u.startsWith('http') ? u : `${backendBase}/${u.replace(/^\//, '')}`));

  const heroBg = resolveAsset(cfg.hero?.bg_url);
  const logo = resolveAsset(cfg.brand?.logo_url);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="landing-page">
      {/* Sticky nav */}
      <header className="sticky top-0 z-40 backdrop-blur bg-background/80 border-b border-border">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            {logo ? (
              <img src={logo} alt={brandName} className="h-8 w-8 rounded object-cover" />
            ) : (
              <div className="h-8 w-8 rounded bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm">
                {brandName.charAt(0)}
              </div>
            )}
            <span className="font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>{brandName}</span>
          </div>
          <nav className="hidden md:flex items-center gap-6 text-sm text-muted-foreground">
            {sections.features !== false && <a href="#features" className="hover:text-foreground">Features</a>}
            {sections.how_it_works !== false && <a href="#how" className="hover:text-foreground">How it works</a>}
            {sections.why_us !== false && <a href="#why" className="hover:text-foreground">Why us</a>}
          </nav>
          <div className="flex items-center gap-2">
            {(cfg.languages || ['en']).length > 1 && (
              <Select value={locale} onValueChange={switchLocale}>
                <SelectTrigger className="w-24 h-9" data-testid="lang-select">
                  <Globe className="h-3 w-3 mr-1" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(cfg.languages || ['en']).map((l) => (
                    <SelectItem key={l} value={l}>{l.toUpperCase()}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle-landing">
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <Button variant="ghost" onClick={() => navigate('/login')} className="hidden md:inline-flex" data-testid="landing-sign-in">Sign in</Button>
            <Button onClick={() => navigate('/register')} data-testid="landing-sign-up">Sign up</Button>
            <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setMobileNav(!mobileNav)}>
              {mobileNav ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>
        {mobileNav && (
          <div className="md:hidden border-t border-border">
            <nav className="flex flex-col p-4 gap-3 text-sm">
              <a href="#features" onClick={() => setMobileNav(false)}>Features</a>
              <a href="#how" onClick={() => setMobileNav(false)}>How it works</a>
              <a href="#why" onClick={() => setMobileNav(false)}>Why us</a>
              <Button variant="outline" onClick={() => navigate('/login')}>Sign in</Button>
            </nav>
          </div>
        )}
      </header>

      {/* Hero */}
      {sections.hero !== false && (
        <section className="relative overflow-hidden">
          {heroBg && cfg.hero?.bg_type === 'image' && (
            <div className="absolute inset-0 -z-10">
              <img src={heroBg} alt="" className="w-full h-full object-cover opacity-25" />
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/40 to-background" />
            </div>
          )}
          {heroBg && cfg.hero?.bg_type === 'video' && (
            <div className="absolute inset-0 -z-10">
              <video autoPlay loop muted playsInline className="w-full h-full object-cover opacity-25">
                <source src={heroBg} />
              </video>
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/40 to-background" />
            </div>
          )}
          <div className="max-w-6xl mx-auto px-4 py-20 md:py-28 grid md:grid-cols-2 gap-10 items-center">
            <div>
              <p className="text-sm uppercase tracking-widest text-primary mb-3">{cfg.brand?.tagline}</p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {cfg.hero?.title}
              </h1>
              <p className="mt-5 text-lg text-muted-foreground max-w-lg">{cfg.hero?.description}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button size="lg" onClick={() => navigate(cfg.hero?.cta_primary_link || '/register')} data-testid="hero-primary-cta">
                  {cfg.hero?.cta_primary_text || 'Create Account'} <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
                <Button size="lg" variant="outline" onClick={() => navigate(cfg.hero?.cta_secondary_link || '/login')} data-testid="hero-secondary-cta">
                  {cfg.hero?.cta_secondary_text || 'Sign In'}
                </Button>
              </div>

              {sections.tvl && tvl && tvl.mode !== 'off' && tvl.value != null && (
                <div className="mt-10 inline-flex items-center gap-3 px-5 py-3 rounded-xl bg-muted/60 border border-border" data-testid="tvl-card">
                  <div className="h-10 w-10 rounded-lg bg-primary/15 flex items-center justify-center">
                    <DollarSign className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{tvl.label || 'Total Value Locked'}</p>
                    <p className="text-xl font-semibold">{formatCurrency(tvl.value, tvl.currency)}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Features */}
      {sections.features !== false && (cfg.features || []).length > 0 && (
        <section id="features" className="max-w-6xl mx-auto px-4 py-20" data-testid="features-section">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Everything you need in one app</h2>
            <p className="mt-3 text-base md:text-lg text-muted-foreground">Pay, shop, get things done and connect, all with one Matrise account.</p>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-2">
            {(cfg.features || [])
              .filter((f) => {
                // Filter by enabled flag AND feature toggle status
                if (f.enabled === false) return false;
                if (f.feature_toggle && cfg.feature_status) {
                  return cfg.feature_status[f.feature_toggle] === true;
                }
                return true;
              })
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((f) => (
                <Card key={f.id} className="border-border/70 hover:border-primary/50 transition-colors" data-testid={`feature-${f.id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="h-12 w-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                        <LangIcon name={f.icon} className="h-6 w-6" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-xl mb-2">{f.title}</h3>
                        <p className="text-sm text-muted-foreground">{f.description}</p>
                        
                        {/* Sub-features for Matrise Runners */}
                        {f.sub_features && f.sub_features.length > 0 && (
                          <div className="mt-4">
                            <button 
                              onClick={() => setExpandedFeature(expandedFeature === f.id ? null : f.id)}
                              className="text-xs font-medium text-primary flex items-center gap-1 hover:underline"
                            >
                              {expandedFeature === f.id ? (
                                <>Hide details <ChevronUp className="h-3 w-3" /></>
                              ) : (
                                <>Show details <ChevronDown className="h-3 w-3" /></>
                              )}
                            </button>
                            {expandedFeature === f.id && (
                              <div className="mt-3 space-y-2 pl-4 border-l-2 border-primary/20">
                                {f.sub_features
                                  .filter(sf => {
                                    // Check if sub-feature toggle is enabled
                                    if (sf.feature_toggle && cfg.feature_status) {
                                      return cfg.feature_status[sf.feature_toggle] === true;
                                    }
                                    return true;
                                  })
                                  .map(sf => (
                                    <div key={sf.id} className="text-sm">
                                      <p className="font-medium text-foreground">{sf.title}</p>
                                      <p className="text-xs text-muted-foreground">{sf.description}</p>
                                    </div>
                                  ))
                                }
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            }
          </div>
        </section>
      )}

      {/* How it works */}
      {sections.how_it_works !== false && (cfg.how_it_works || []).length > 0 && (
        <section id="how" className="bg-muted/40 py-20" data-testid="how-section">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>How it works</h2>
              <p className="mt-3 text-base md:text-lg text-muted-foreground">Get started in three simple steps.</p>
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              {(cfg.how_it_works || []).filter((s) => s.enabled !== false).sort((a, b) => (a.order || 0) - (b.order || 0)).map((s, idx) => (
                <div key={s.id} className="relative p-6 rounded-xl bg-background border border-border hover:border-primary/50 transition-colors">
                  <div className="absolute -top-3 -left-3 h-9 w-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold text-sm">
                    {idx + 1}
                  </div>
                  <h3 className="font-semibold text-lg mt-3">{s.title}</h3>
                  <p className="text-sm text-muted-foreground mt-2">{s.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Why us */}
      {sections.why_us !== false && (cfg.why_us || []).length > 0 && (
        <section id="why" className="max-w-6xl mx-auto px-4 py-20" data-testid="why-section">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Why choose {brandName}?</h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
            {(cfg.why_us || []).filter((w) => w.enabled !== false).sort((a, b) => (a.order || 0) - (b.order || 0)).map((w) => (
              <div key={w.id} className="text-center p-4">
                <div className="h-12 w-12 rounded-full bg-primary/15 text-primary mx-auto flex items-center justify-center mb-3">
                  <LangIcon name={w.icon} className="h-5 w-5" />
                </div>
                <h3 className="font-medium">{w.title}</h3>
                <p className="text-xs text-muted-foreground mt-1">{w.description}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Download */}
      {sections.download && cfg.download?.enabled && (
        <section className="bg-primary/5 py-16">
          <div className="max-w-6xl mx-auto px-4 text-center">
            <h2 className="text-2xl md:text-3xl font-semibold">Get the app</h2>
            <p className="mt-2 text-muted-foreground">Available on all your devices.</p>
            <div className="mt-6 flex flex-wrap gap-3 justify-center">
              {cfg.download.google_play_url && (
                <a href={cfg.download.google_play_url} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline">Google Play</Button>
                </a>
              )}
              {cfg.download.app_store_url && (
                <a href={cfg.download.app_store_url} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline">App Store</Button>
                </a>
              )}
              {cfg.download.apk_url && (
                <a href={cfg.download.apk_url} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline">APK Download</Button>
                </a>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      {sections.footer !== false && (
        <footer className="border-t border-border py-8 mt-4">
          <div className="max-w-6xl mx-auto px-4 flex flex-wrap items-center justify-between gap-4 text-sm text-muted-foreground">
            <p>{cfg.footer?.copyright}</p>
            <div className="flex gap-5">
              {cfg.footer?.privacy_url && <Link to={cfg.footer.privacy_url} className="hover:text-foreground">Privacy</Link>}
              {cfg.footer?.terms_url && <Link to={cfg.footer.terms_url} className="hover:text-foreground">Terms</Link>}
              {cfg.footer?.support_url && <Link to={cfg.footer.support_url} className="hover:text-foreground">Support</Link>}
            </div>
          </div>
        </footer>
      )}
    </div>
  );
};

export default LandingPage;
